import { useAtom, useSetAtom } from 'jotai'
import { match, P } from 'ts-pattern'
import {
  Text,
  Button,
  Card,
  GreyGrey80,
  GreyWhite,
  IconBank,
  Image,
  Primary13SemiBold,
  TextAreaInput,
  TextFieldInput,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import stripeLogo from '../../assets/stripeLogo.svg'
import goCardlessLogo from 'Integrations/integrationsConfig/goCardless/goCardlessLogo.svg'
import { currentEditorStepAtom } from '../../editor.atoms'
import { Step } from '../../components'
import { InvoiceMerchantDetailsModal, RowControl } from 'Invoices/components'
import { clientInvoiceAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { StripePaymentSettingsSwitch } from 'Invoices/components/PaymentSettings/StripePaymentSettingsSwitch'
import { GoCardlessPaymentSettingsSwitch } from 'Invoices/components/PaymentSettings/GoCardlessPaymentSettingsSwitch'
import {
  useStripeIntegration,
  useGoCardlessIntegration
} from 'modules/Integrations'
import {
  CurrentInvoiceDatePicker,
  CurrentInvoiceDueDatePicker
} from 'Invoices/components/CreateEditShared'
import { useInvoiceDueDateTerms } from 'Invoices/hooks/useInvoiceDueDateTerms'

export const StepTerms = () => {
  const [clientInvoice, partialUpdateInvoice] = useAtom(clientInvoiceAtom)
  const { calculatedFrom } = useInvoiceDueDateTerms()

  const { enabled: stripeIntegrationEnabled } = useStripeIntegration()
  const { enabled: goCardlessIntegrationEnabled } = useGoCardlessIntegration()

  if (!clientInvoice) {
    return null
  }

  return (
    <Step title="Invoice terms" controls={<Controls />}>
      <Step.Section>
        <Grid gridTemplateColumns="repeat(2, 1fr)" gap="16px">
          <GridItem>
            <Flex flex="1" flexDir="column" gap="8px">
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                Invoice date
              </Text>

              <CurrentInvoiceDatePicker data-testid="invoiceTerms.invoiceDate" />
            </Flex>
          </GridItem>

          <GridItem>
            <Flex flex="1" flexDir="column" gap="8px">
              <Text
                {...Primary13SemiBold}
                color={GreyGrey80}
                data-testid="invoiceTerms.dueDate.label"
              >
                {match(calculatedFrom)
                  .with('ISSUE_DATE', () => 'Payment terms')
                  .with('ACCOUNTING_DATE', P.nullish, () => 'Due date')
                  .exhaustive()}
              </Text>

              <CurrentInvoiceDueDatePicker
                width="100%"
                data-testid="invoiceTerms.dueDate"
              />
            </Flex>
          </GridItem>
        </Grid>
      </Step.Section>

      <Step.Section title="Payment method">
        <Card variant="v2" padding="0 16px">
          {stripeIntegrationEnabled && (
            <RowControl
              icon={<Image src={stripeLogo} width="100%" height="100%" />}
              title="Accept payments via Stripe"
              action={<StripePaymentSettingsSwitch />}
            />
          )}
          {goCardlessIntegrationEnabled && (
            <RowControl
              icon={<Image src={goCardlessLogo} width="100%" height="100%" />}
              iconBackground={GreyWhite}
              title="Accept payments via GoCardless"
              action={<GoCardlessPaymentSettingsSwitch />}
            />
          )}
          {clientInvoice.customerBillingAddress && (
            <RowControl
              hideBorderBottom
              icon={<IconBank />}
              title="Show bank details"
              action={
                <InvoiceMerchantDetailsModal
                  trigger={<Button variant="secondary">View</Button>}
                  currency={clientInvoice.currency}
                  country={clientInvoice.customerBillingAddress.country}
                />
              }
            />
          )}
        </Card>
      </Step.Section>

      <Step.Section title="Additional options">
        <Flex gap="8px" flexDir="column">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            Memo (optional)
          </Text>
          <TextAreaInput
            data-testid="invoiceTerms.memo"
            onChange={value => {
              partialUpdateInvoice({ memo: value })
            }}
            value={clientInvoice.memo}
            maxLength={10000}
            placeholder="Add memo"
          />
        </Flex>

        <Grid gridTemplateColumns="repeat(2, 1fr)" gap="16px">
          <GridItem>
            <Flex flex="1" flexDir="column" gap="8px">
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                PO Number (optional)
              </Text>

              <TextFieldInput
                data-testid="invoiceTerms.poNumber"
                placeholder="Set PO number"
                value={clientInvoice.purchaseOrderNumber}
                onChange={e => {
                  partialUpdateInvoice({ purchaseOrderNumber: e.target.value })
                }}
              />
            </Flex>
          </GridItem>

          <GridItem>
            <Flex flex="1" flexDir="column" gap="8px">
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                Reference (optional)
              </Text>

              <TextFieldInput
                data-testid="invoiceTerms.reference"
                placeholder="Set reference"
                value={clientInvoice.reference}
                onChange={e => {
                  partialUpdateInvoice({ reference: e.target.value })
                }}
              />
            </Flex>
          </GridItem>
        </Grid>
      </Step.Section>
    </Step>
  )
}

const Controls = () => {
  const setStep = useSetAtom(currentEditorStepAtom)

  return (
    <Step.Controls
      back={
        <Step.Controls.Back
          label="Back"
          onClick={() => setStep('CUSTOMER_AND_LINE_ITEMS')}
          keyboardShortcut={['Mod', '[']}
        />
      }
      forward={
        <Step.Controls.Forward
          onClick={() => setStep('SUMMARY')}
          label="Review"
          keyboardShortcut={['Mod', ']']}
        />
      }
    />
  )
}
